<script setup lang="ts">
import { useAuthStore } from "../../stores/auth";
import { useThemeStore } from "../../stores/theme";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { computed, ref } from "vue";
import { LogOut, ChevronDown, Building2, Sun, Moon, Settings } from "lucide-vue-next";
import AiSpendWidget from "./AiSpendWidget.vue";

const auth = useAuthStore();
const theme = useThemeStore();
const router = useRouter();
const { t } = useI18n();
const menuOpen = ref(false);
const tenantMenuOpen = ref(false);
const switching = ref(false);

// Theme toggle: light ↔ dark only. Icon + tooltip reflect the CURRENT theme.
const themeIcon = computed(() => (theme.isDark ? Moon : Sun));
const themeLabel = computed(() => (theme.isDark ? t("theme.dark") : t("theme.light")));

// Close a dropdown when clicking anywhere OUTSIDE its wrapper. The trigger button
// and the menu both live inside the wrapper, so clicking the trigger still toggles
// (no race), and a click anywhere else closes the menu — standard dropdown UX.
const vClickOutside = {
  mounted(el: HTMLElement & { _co?: (e: MouseEvent) => void }, binding: { value: () => void }) {
    el._co = (e: MouseEvent) => {
      if (!el.contains(e.target as Node)) binding.value();
    };
    document.addEventListener("click", el._co);
  },
  unmounted(el: HTMLElement & { _co?: (e: MouseEvent) => void }) {
    if (el._co) document.removeEventListener("click", el._co);
  },
};

async function logout(): Promise<void> {
  await auth.logout();
  menuOpen.value = false;
  router.push({ name: "login" });
}

const availableTenants = computed(() => auth.user?.available_tenants ?? []);
const currentTenantSlug = computed(() => auth.user?.tenant_slug ?? null);
const homeTenantId = computed(() => auth.user?.home_tenant_id ?? null);

// Поиск по организациям — при большом их числе. Плюс список скроллится (см. шаблон),
// чтобы нижние организации не уходили за экран.
const tenantQuery = ref("");
const TENANT_SEARCH_THRESHOLD = 8;
const filteredTenants = computed(() => {
  const q = tenantQuery.value.trim().toLowerCase();
  if (!q) return availableTenants.value;
  return availableTenants.value.filter(
    (t) => (t.display_name ?? "").toLowerCase().includes(q) || (t.slug ?? "").toLowerCase().includes(q),
  );
});

async function switchToTenant(slug: string): Promise<void> {
  if (switching.value) return;
  switching.value = true;
  tenantMenuOpen.value = false;
  try {
    // Pass null when picking own home — clears the override entirely.
    const home = availableTenants.value.find((t) => t.id === homeTenantId.value);
    const targetSlug = home && home.slug === slug ? null : slug;
    await auth.switchTenant(targetSlug);
    // Hard reload to drop any in-memory state from the previous tenant.
    window.location.reload();
  } catch (e) {
    console.error("switchTenant failed", e);
    switching.value = false;
  }
}
</script>

<template>
  <header class="border-b border-surface-200 bg-surface-0">
    <div class="flex items-center justify-between px-6 py-3">
      <!-- Tenant indicator + super_admin switcher -->
      <div class="flex items-center gap-2">
        <!-- Super admin: dropdown to switch among available tenants -->
        <div v-if="auth.isSuperAdmin && availableTenants.length > 1" class="relative" v-click-outside="() => (tenantMenuOpen = false)">
          <button
            type="button"
            class="flex items-center gap-1.5 rounded-lg border border-accent-200 bg-accent-50 px-2.5 py-1 text-xs font-semibold text-accent-700 hover:bg-accent-100"
            :disabled="switching"
            data-testid="tenant-switcher-trigger"
            @click="tenantMenuOpen = !tenantMenuOpen"
          >
            <Building2 class="size-3.5" />
            <span>{{ currentTenantSlug ?? "—" }}</span>
            <ChevronDown class="size-3.5" />
          </button>
          <div
            v-if="tenantMenuOpen"
            class="absolute left-0 mt-2 w-64 rounded-lg border border-surface-200 bg-surface-0 shadow-elevated py-1 z-50 flex flex-col max-h-[75vh]"
          >
            <div class="px-3 py-2 border-b border-surface-100 shrink-0">
              <div class="text-xs font-semibold text-surface-500 uppercase tracking-wide">Организация</div>
              <div class="text-xs text-surface-400 mt-0.5">Переключение видно в audit log</div>
            </div>
            <div v-if="availableTenants.length > TENANT_SEARCH_THRESHOLD" class="px-2 py-2 border-b border-surface-100 shrink-0">
              <input
                v-model="tenantQuery"
                type="text"
                class="input w-full text-sm"
                placeholder="Поиск организации…"
                data-testid="tenant-search"
              />
            </div>
            <div class="overflow-y-auto">
              <button
                v-for="t in filteredTenants"
                :key="t.id"
                type="button"
                class="w-full text-left px-3 py-2 hover:bg-surface-50 flex items-center justify-between gap-2"
                :class="{ 'bg-accent-50 text-accent-800 font-semibold': t.slug === currentTenantSlug }"
                :data-testid="`tenant-option-${t.slug}`"
                @click="switchToTenant(t.slug)"
              >
                <div class="min-w-0 flex-1">
                  <div class="text-sm truncate">{{ t.display_name }}</div>
                  <div class="text-xs text-surface-400 font-mono">{{ t.slug }}</div>
                </div>
                <span v-if="t.id === homeTenantId" class="badge-neutral text-[10px] uppercase">home</span>
              </button>
              <div v-if="filteredTenants.length === 0" class="px-3 py-3 text-sm text-surface-400" data-testid="tenant-none">
                Ничего не найдено
              </div>
            </div>
          </div>
        </div>
        <!-- Plain badge for non-super-admin OR single-tenant super_admin -->
        <span v-else-if="auth.user?.tenant_slug" class="badge-accent">
          {{ auth.user.tenant_slug }}
        </span>
        <span v-if="auth.isSuperAdmin" class="badge-info">{{ t("settings.role.super_admin") }}</span>
      </div>

      <!-- Right side: расход на ИИ + theme toggle + user menu -->
      <div class="flex items-center gap-1.5">
        <!-- №18c (Игорь) — общий расход организации на ИИ (сегодня), слева от переключателя темы. -->
        <AiSpendWidget />
        <!-- Theme toggle — cycles light → dark → system -->
        <button
          type="button"
          class="flex items-center justify-center size-9 rounded-lg text-surface-500 hover:bg-surface-100 hover:text-surface-900 transition-colors"
          :title="themeLabel"
          :aria-label="themeLabel"
          data-testid="theme-toggle"
          @click="theme.cycle()"
        >
          <component :is="themeIcon" class="size-5" :stroke-width="2" />
        </button>

        <div class="relative" v-click-outside="() => (menuOpen = false)">
        <button
          class="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-surface-100 transition-colors"
          data-test="user-menu-trigger"
          @click="menuOpen = !menuOpen"
        >
          <div class="size-8 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center text-sm font-semibold">
            {{ auth.user?.display_name?.[0]?.toUpperCase() ?? "?" }}
          </div>
          <span class="text-sm font-medium text-surface-700 hidden sm:inline">
            {{ auth.user?.display_name }}
          </span>
          <ChevronDown class="size-4 text-surface-400" />
        </button>

        <Transition
          enter-active-class="transition duration-100 ease-out"
          enter-from-class="opacity-0 translate-y-1"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition duration-75 ease-in"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div
            v-if="menuOpen"
            class="absolute right-0 mt-2 w-56 rounded-lg border border-surface-200 bg-surface-0 shadow-elevated py-1 z-50"
          >
            <div class="px-3 py-2 border-b border-surface-100">
              <div class="text-sm font-medium text-surface-900">{{ auth.user?.display_name }}</div>
              <div class="text-xs text-surface-500">{{ auth.user?.login }}</div>
            </div>
            <!-- Б5: вход на «Настройки» (там карточка 2FA/TOTP). Страница была
                 осиротевшей — сайдбар её вырезает, а в этом меню был только «Выйти»,
                 поэтому владелец не мог найти, как включить второй фактор. -->
            <RouterLink
              :to="{ name: 'settings' }"
              data-test="header-settings-link"
              class="w-full text-left px-3 py-2 text-sm text-surface-700 hover:bg-surface-50 flex items-center gap-2"
              @click="menuOpen = false"
            >
              <Settings class="size-4" />
              {{ t("nav.settings") }}
            </RouterLink>
            <button
              class="w-full text-left px-3 py-2 text-sm text-surface-700 hover:bg-surface-50 flex items-center gap-2"
              @click="logout"
            >
              <LogOut class="size-4" />
              {{ t("auth.logout") }}
            </button>
          </div>
        </Transition>
        </div>
      </div>
    </div>
  </header>
</template>
